import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { toLocalYMD } from '../lib/dates';
import { 
  Search, ArrowUpDown, Phone, Mail, 
  MessageSquare, CreditCard, Calendar, AlertTriangle,
  ChevronRight, PoundSterling, Clock, Users, TrendingUp,
  Menu, Filter, Loader2, RefreshCw, X, CheckCircle2, Banknote, Check, Send, MoreVertical
} from 'lucide-react';
import { zoneColors } from '../data/csvData';
import * as api from '../services/api';
import PaymentModal from '../components/modals/PaymentModal';
import TakePaymentModal from '../components/TakePaymentModal';
import WhatsAppComposeModal from '../components/WhatsAppComposeModal';
import InvoiceDeliveryRow from '../components/InvoiceDeliveryRow';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Payment method options
const PAYMENT_METHODS = [
  { id: 'cash', label: 'Cash', icon: '💵' },
  { id: 'bank_transfer', label: 'Bank Transfer', icon: '🏦' },
  { id: 'card', label: 'Card Payment', icon: '💳' },
  { id: 'bacs', label: 'BACS', icon: '🔄' },
  { id: 'standing_order', label: 'Standing Order', icon: '📋' },
  { id: 'direct_debit', label: 'Direct Debit', icon: '🔁' },
  { id: 'cheque', label: 'Cheque', icon: '📝' },
  { id: 'other', label: 'Other', icon: '📌' },
];

const Owings = ({ onSelectCustomer, user, onMenuClick, setCurrentPage }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('debt-oldest');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [owingsData, setOwingsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // User role check
  const isWorker = user?.role === 'Worker';
  
  // VAT settings
  const [vatEnabled, setVatEnabled] = useState(false);
  const [vatPercentage, setVatPercentage] = useState(20);
  
  // Mark as Paid modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  // Request Payment modal state
  const [showRequestPaymentModal, setShowRequestPaymentModal] = useState(false);
  const [requestPaymentCustomer, setRequestPaymentCustomer] = useState(null);
  const [requestPaymentMethod, setRequestPaymentMethod] = useState('both'); // 'email', 'sms', 'both'
  const [isRequestingPayment, setIsRequestingPayment] = useState(false);
  const [requestPaymentSuccess, setRequestPaymentSuccess] = useState(false);
  // Preview-before-send: fetched rendered SMS/email content. When non-null,
  // the modal shows the preview screen with "Confirm & Send" instead of
  // immediately firing the request. Cleared when modal closes / customer
  // changes / channel selection changes (user might switch SMS → Email after
  // previewing and we want to fetch fresh content).
  const [requestPaymentPreview, setRequestPaymentPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [requestPaymentNote, setRequestPaymentNote] = useState('');
  const [chaseInvoices, setChaseInvoices] = useState([]); // unpaid invoices for the customer
  const [chaseSelectedIds, setChaseSelectedIds] = useState([]); // which ones we're chasing
  
  // SMS modal state
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsCustomer, setSmsCustomer] = useState(null);
  const [smsMessage, setSmsMessage] = useState('');
  const [isSendingSms, setIsSendingSms] = useState(false);
  
  // WhatsApp modal state
  const [showWaModal, setShowWaModal] = useState(false);
  const [waCustomer, setWaCustomer] = useState(null);
  
  // Take Payment modal state
  const [showTakePayment, setShowTakePayment] = useState(false);
  const [takePaymentCustomer, setTakePaymentCustomer] = useState(null);
  
  // Mobile kebab-menu: which customer's "more actions" dropdown is open.
  // We use a single id (not a Set) because only one menu can be visible
  // at a time on a phone screen. Closing on outside-click is handled by
  // a global mousedown listener below.
  const [openMobileMenuFor, setOpenMobileMenuFor] = useState(null);
  
  // Bank reconciliation state
  const [showReconcileModal, setShowReconcileModal] = useState(false);
  const [reconcileStep, setReconcileStep] = useState('date-select'); // 'date-select', 'searching', 'results'
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileData, setReconcileData] = useState(null);
  const [processingMatch, setProcessingMatch] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [connectedBanks, setConnectedBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('');  const [selectingCustomerFor, setSelectingCustomerFor] = useState(null);
  const [allCustomers, setAllCustomers] = useState([]);
  const [searchProgress, setSearchProgress] = useState({ current: 0, total: 0, currentTxn: '' });
  
  // Date range for reconciliation
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toLocalYMD(d);
  });
  const [dateTo, setDateTo] = useState(() => toLocalYMD(new Date()));

  const handleCustomerClick = (customer) => {
    if (onSelectCustomer) {
      onSelectCustomer(customer);
    }
  };
  
  // Open payment modal for a customer
  const openPaymentModal = (customer, e) => {
    e.stopPropagation();
    setSelectedCustomer(customer);
    setShowPaymentModal(true);
  };
  
  // Handle payment confirmation from PaymentModal component
  const handlePaymentConfirm = async (paymentData) => {
    setIsProcessingPayment(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/customers/${paymentData.customerId}/mark-paid`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(paymentData.amount),
          payment_method: paymentData.method,
          notes: paymentData.notes || null,
          send_receipt: !!paymentData.sendReceipt,
          invoice_ids: paymentData.invoiceIds && paymentData.invoiceIds.length > 0 ? paymentData.invoiceIds : null
        })
      });
      
      if (response.ok) {
        // Refresh the customer list
        fetchOwings();
        return true;
      } else {
        const errorData = await response.json();
        alert(`Payment failed: ${errorData.detail || 'Unknown error'}`);
        return false;
      }
    } catch (err) {
      console.error('Payment error:', err);
      alert('Error processing payment');
      return false;
    } finally {
      setIsProcessingPayment(false);
    }
  };
  
  // Open Request Payment modal
  const openRequestPaymentModal = (customer, e) => {
    e.stopPropagation();
    setRequestPaymentCustomer(customer);
    setRequestPaymentMethod('both');
    setRequestPaymentSuccess(false);
    setRequestPaymentPreview(null);
    setRequestPaymentNote('');
    setChaseInvoices([]);
    setChaseSelectedIds([]);
    setShowRequestPaymentModal(true);
    // Load the customer's unpaid invoices so the user can pick which to chase
    (async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/invoices?customer_id=${customer.id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const all = await res.json();
          const unpaid = (all || []).filter(i => ['unpaid', 'pending', 'overdue'].includes(i.status));
          unpaid.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
          setChaseInvoices(unpaid);
          setChaseSelectedIds(unpaid.map(i => i.id)); // all selected by default
        }
      } catch (err) {
        console.error('Failed to load unpaid invoices for chase picker:', err);
      }
    })();
  };
  
  // Step 1 of the "Request Payment" flow — fetches the rendered SMS / email
  // text from the backend (using the user's saved Payment Reminder Templates
  // and the customer's outstanding balance / pay-link / invoice list) so the
  // user can review BEFORE the messages actually go out. Sets
  // requestPaymentPreview which the modal renders as a preview screen with
  // "Back" / "Confirm & Send" buttons.
  const fetchRequestPaymentPreview = async () => {
    if (!requestPaymentCustomer) return;
    setIsLoadingPreview(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const qs = new URLSearchParams();
      if (requestPaymentNote.trim()) qs.set('note', requestPaymentNote.trim());
      if (chaseInvoices.length > 0 && chaseSelectedIds.length > 0 && chaseSelectedIds.length < chaseInvoices.length) {
        qs.set('invoice_ids', chaseSelectedIds.join(','));
      }
      const res = await fetch(
        `${API_URL}/api/customers/${requestPaymentCustomer.id}/payment-reminder-preview${qs.toString() ? `?${qs.toString()}` : ''}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setRequestPaymentPreview(data);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`Couldn't load preview: ${err.detail || res.statusText}`);
      }
    } catch (err) {
      console.error('Preview fetch error:', err);
      alert('Failed to load preview');
    } finally {
      setIsLoadingPreview(false);
    }
  };
  
  // Step 2 — actually sends the payment-reminder via the chosen channel(s).
  // Routes through the unified backend endpoint which:
  //   • renders the user-editable templates (Settings → Payment Reminder Templates)
  //   • injects a Stripe pay-now link for the oldest unpaid invoice
  //   • includes a bulleted list of every unpaid invoice (email/SMS)
  //   • falls back to SMS automatically if WhatsApp template is rejected
  const handleSendRequestPayment = async () => {
    if (!requestPaymentCustomer) return;
    
    setIsRequestingPayment(true);
    const token = localStorage.getItem('streakfree_token');
    
    try {
      const res = await fetch(
        `${API_URL}/api/customers/${requestPaymentCustomer.id}/send-payment-reminder`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            method: requestPaymentMethod,
            note: requestPaymentNote.trim() || null,
            invoice_ids: (chaseInvoices.length > 0 && chaseSelectedIds.length > 0 && chaseSelectedIds.length < chaseInvoices.length)
              ? chaseSelectedIds
              : null,
          })
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        const anyOk = Object.values(data.results || {}).some(r => r && r.ok);
        if (anyOk) {
          setRequestPaymentSuccess(true);
          fetchOwings(); // refresh so the "Chased today" marker appears immediately
          setTimeout(() => {
            setShowRequestPaymentModal(false);
            setRequestPaymentCustomer(null);
            setRequestPaymentSuccess(false);
            setRequestPaymentPreview(null);
          }, 2000);
        } else {
          alert('Failed to send payment request — no channels succeeded. Check the customer has the right email/phone.');
        }
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`Failed to send payment request: ${err.detail || res.statusText}`);
      }
    } catch (err) {
      console.error('Request payment error:', err);
      alert('Error sending payment request');
    } finally {
      setIsRequestingPayment(false);
    }
  };
  
  // Handle call button
  const handleCall = (customer, e) => {
    e.stopPropagation();
    if (customer.phone) {
      window.location.href = `tel:${customer.phone}`;
    } else {
      alert('No phone number available for this customer');
    }
  };
  
  // Handle email button — fetches the rendered template from the backend so
  // it matches what Settings → Payment Reminder Templates has saved (instead
  // of the previous hardcoded text that ignored the user's customised wording).
  const handleEmail = async (customer, e) => {
    e.stopPropagation();
    if (!customer.email) {
      alert('No email address available for this customer');
      return;
    }
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(
        `${API_URL}/api/customers/${customer.id}/payment-reminder-preview`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        const subject = encodeURIComponent(data.email_subject || '');
        const body = encodeURIComponent(data.email_body || '');
        window.location.href = `mailto:${customer.email}?subject=${subject}&body=${body}`;
        return;
      }
      // If preview fails (e.g. no balance, no template), fall back to a
      // minimal subject so the user can still draft something.
      const subject = encodeURIComponent(`Outstanding Balance - £${(customer.balance || 0).toFixed(2)}`);
      window.location.href = `mailto:${customer.email}?subject=${subject}`;
    } catch (err) {
      console.error('Email preview error:', err);
      const subject = encodeURIComponent(`Outstanding Balance - £${(customer.balance || 0).toFixed(2)}`);
      window.location.href = `mailto:${customer.email}?subject=${subject}`;
    }
  };
  
  // Handle SMS button — fetches the rendered template from the backend so
  // the modal pre-fills with the user's saved Payment Reminder SMS template
  // (including the pay-now link), not the previous hardcoded text.
  const handleSms = async (customer, e) => {
    e.stopPropagation();
    if (!customer.phone) {
      alert('No phone number available for this customer');
      return;
    }
    setSmsCustomer(customer);
    // Open the modal immediately with a loading placeholder so the UI feels
    // responsive even on slow connections.
    setSmsMessage('Loading saved template…');
    setShowSmsModal(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(
        `${API_URL}/api/customers/${customer.id}/payment-reminder-preview`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setSmsMessage(data.sms_body || '');
      } else {
        // Fallback to a sensible default if the preview endpoint errors
        // (e.g. customer has no balance, network blip).
        setSmsMessage(
          `Hi ${customer.name.split(' ')[0]}, just a reminder you have an outstanding balance of £${(customer.balance || 0).toFixed(2)}. Please let us know if you have any questions.`
        );
      }
    } catch (err) {
      console.error('SMS preview error:', err);
      setSmsMessage(
        `Hi ${customer.name.split(' ')[0]}, just a reminder you have an outstanding balance of £${(customer.balance || 0).toFixed(2)}. Please let us know if you have any questions.`
      );
    }
  };
  
  // Handle WhatsApp button — opens compose modal pre-filled with payment_reminder template
  const handleWhatsApp = (customer, e) => {
    e.stopPropagation();
    if (!customer.phone) {
      alert('No phone number available for this customer');
      return;
    }
    setWaCustomer(customer);
    setShowWaModal(true);
  };
  
  // Handle Take Payment button
  const handleTakePayment = (customer, e) => {
    e.stopPropagation();
    setTakePaymentCustomer(customer);
    setShowTakePayment(true);
  };
  
  // Send SMS
  const handleSendSms = async () => {
    if (!smsCustomer || !smsMessage) return;
    
    setIsSendingSms(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/sms/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_phone: smsCustomer.phone,
          message: smsMessage,
          customer_id: smsCustomer.id
        })
      });
      
      if (response.ok) {
        alert('SMS sent successfully!');
        setShowSmsModal(false);
        setSmsCustomer(null);
        setSmsMessage('');
      } else {
        const errorData = await response.json();
        alert(`Failed to send SMS: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('SMS error:', err);
      alert('Error sending SMS');
    } finally {
      setIsSendingSms(false);
    }
  };
  
  // Handle payment card icon (open payment modal)
  const handlePaymentIcon = (customer, e) => {
    openPaymentModal(customer, e);
  };
  
  // Close payment modal
  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedCustomer(null);
    setPaymentAmount('');
    setPaymentNotes('');
    setPaymentSuccess(false);
  };
  
  // Process payment
  const handleMarkAsPaid = async () => {
    if (!selectedCustomer || !paymentAmount) return;
    
    setIsProcessingPayment(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/customers/${selectedCustomer.id}/mark-paid`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(paymentAmount),
          payment_method: paymentMethod,
          notes: paymentNotes || null
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to record payment');
      }
      
      // Show success state
      setPaymentSuccess(true);
      
      // After a brief delay, close modal and refresh data
      setTimeout(async () => {
        closePaymentModal();
        // Refresh owings data
        const data = await api.getOwings();
        setOwingsData(data);
      }, 1500);
      
    } catch (err) {
      console.error('Payment error:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setIsProcessingPayment(false);
    }
  };
  
  // Bank Reconciliation Functions
  const openReconcileModal = async () => {
    setShowReconcileModal(true);
    setReconcileStep('date-select');
    setReconcileData(null);
    setSearchProgress({ current: 0, total: 0, currentTxn: '' });
    
    // Fetch connected banks
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/truelayer/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConnectedBanks(data.banks || []);
        // Select first bank by default
        if (data.banks?.length > 0 && !selectedBankId) {
          setSelectedBankId(data.banks[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching banks:', err);
    }
  };
  
  // Close reconcile modal
  const closeReconcileModal = () => {
    setShowReconcileModal(false);
  };
  
  const startReconcileSearch = async () => {
    setReconcileStep('searching');
    setReconcileLoading(true);
    setSearchProgress({ current: 0, total: 0, currentTxn: 'Connecting to bank...' });
    
    try {
      const token = localStorage.getItem('streakfree_token');
      
      // Calculate days from date range
      const from = new Date(dateFrom);
      const to = new Date(dateTo);
      const days = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1;
      
      // Simulate search progress animation
      const progressInterval = setInterval(() => {
        setSearchProgress(prev => {
          if (prev.current >= 100) {
            clearInterval(progressInterval);
            return prev;
          }
          const transactions = [
            'Checking recent deposits...',
            'Scanning bank transfers...',
            'Matching customer references...',
            'Applying learned patterns...',
            'Finding name matches...',
            'Processing results...'
          ];
          return {
            current: Math.min(prev.current + Math.random() * 15, 95),
            total: 100,
            currentTxn: transactions[Math.floor(prev.current / 20)] || 'Finalizing...'
          };
        });
      }, 300);
      
      // Fetch potential matches - include bank_id if selected
      const bankParam = selectedBankId ? `&bank_id=${selectedBankId}` : '';
      const response = await fetch(`${API_URL}/api/reconciliation/find-payments?days=${days}${bankParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      clearInterval(progressInterval);
      setSearchProgress({ current: 100, total: 100, currentTxn: 'Complete!' });
      
      if (response.ok) {
        const data = await response.json();
        
        // Also fetch all customers for manual selection
        const custResponse = await fetch(`${API_URL}/api/customers?has_balance=true`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (custResponse.ok) {
          setAllCustomers(await custResponse.json());
        }
        
        // Separate auto-matches from those needing confirmation
        const autoMatches = data.matches?.filter(m => (m.match_type === 'auto_ref' || m.match_type === 'auto_rule') && m.customer) || [];
        const needsConfirmation = data.matches?.filter(m => m.match_type === 'suggested' || !m.match_type) || [];
        
        // Apply auto-matches and track what was applied
        const appliedMatches = [];
        
        for (const match of autoMatches) {
          try {
            const confirmRes = await fetch(
              `${API_URL}/api/reconciliation/confirm-match?transaction_id=${match.transaction_id}&customer_id=${match.customer.id}&amount=${match.amount}&learn_pattern=false`,
              {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
              }
            );
            if (confirmRes.ok) {
              const result = await confirmRes.json();
              appliedMatches.push({
                ...match,
                previousBalance: result.previous_balance,
                newBalance: result.new_balance
              });
            }
          } catch (e) {
            console.error('Auto-match error:', e);
          }
        }
        
        // Set data with both applied and pending matches
        setReconcileData({
          ...data,
          appliedMatches: appliedMatches,
          pendingMatches: needsConfirmation,
          stats: {
            ...data.stats,
            auto_applied_count: appliedMatches.length,
            auto_applied_total: appliedMatches.reduce((sum, m) => sum + m.amount, 0),
            needs_confirmation: needsConfirmation.length
          }
        });
        
        // Refresh owings if any auto-matches were applied
        if (appliedMatches.length > 0) {
          const owingsData = await api.getOwings();
          setOwingsData(owingsData);
        }
        
        // Short delay then show results
        setTimeout(() => {
          setReconcileStep('results');
        }, 500);
        
      } else {
        const err = await response.json();
        setReconcileData({ status: 'error', message: err.detail || 'Failed to find payments' });
        setReconcileStep('results');
      }
    } catch (err) {
      console.error('Reconcile error:', err);
      setReconcileData({ status: 'error', message: err.message });
      setReconcileStep('results');
    } finally {
      setReconcileLoading(false);
    }
  };
  
  const confirmMatch = async (match, customerId = null, learnPattern = true) => {
    const targetCustomerId = customerId || match.customer?.id;
    if (!targetCustomerId) return;
    
    setProcessingMatch(match.transaction_id);
    
    try {
      const token = localStorage.getItem('streakfree_token');
      
      // Get customer name for the applied match display
      const targetCustomer = customerId 
        ? allCustomers.find(c => c.id === customerId)
        : match.customer;
      
      // Confirm the match
      const response = await fetch(
        `${API_URL}/api/reconciliation/confirm-match?transaction_id=${match.transaction_id}&customer_id=${targetCustomerId}&amount=${match.amount}&learn_pattern=${learnPattern}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        
        // Learn the pattern for future auto-matching
        if (learnPattern && match.description) {
          const pattern = match.merchant_name || match.description.split(' ').slice(0, 3).join(' ');
          if (pattern && pattern.length >= 4) {
            await fetch(
              `${API_URL}/api/reconciliation/learn-match?transaction_id=${match.transaction_id}&customer_id=${targetCustomerId}&pattern=${encodeURIComponent(pattern)}`,
              {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
              }
            );
          }
        }
        
        // Move from pending to applied
        const appliedMatch = {
          ...match,
          customer: targetCustomer || match.customer,
          match_type: 'manual',
          previousBalance: result.previous_balance,
          newBalance: result.new_balance
        };
        
        setReconcileData(prev => ({
          ...prev,
          appliedMatches: [...(prev.appliedMatches || []), appliedMatch],
          pendingMatches: (prev.pendingMatches || []).filter(m => m.transaction_id !== match.transaction_id),
          stats: {
            ...prev.stats,
            auto_applied_count: (prev.stats?.auto_applied_count || 0) + 1,
            auto_applied_total: (prev.stats?.auto_applied_total || 0) + match.amount,
            needs_confirmation: Math.max(0, (prev.stats?.needs_confirmation || 0) - 1)
          }
        }));
        
        // Refresh owings
        const data = await api.getOwings();
        setOwingsData(data);
      } else {
        const err = await response.json();
        alert(err.detail || 'Failed to confirm match');
      }
    } catch (err) {
      console.error('Confirm match error:', err);
      alert(err.message);
    } finally {
      setProcessingMatch(null);
      setSelectingCustomerFor(null);
    }
  };
  
  const ignoreTransaction = async (match) => {
    setProcessingMatch(match.transaction_id);
    
    try {
      const token = localStorage.getItem('streakfree_token');
      const senderName = match.sender_name || match.description || '';
      
      const response = await fetch(
        `${API_URL}/api/reconciliation/ignore-transaction?transaction_id=${match.transaction_id}&sender_name=${encodeURIComponent(senderName)}&ignore_pattern=true`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        // Remove ALL transactions from this sender from the pending list
        const senderLower = senderName.toLowerCase();
        
        setReconcileData(prev => {
          const remainingMatches = (prev.pendingMatches || []).filter(m => {
            const mSender = (m.sender_name || m.description || '').toLowerCase();
            // Remove if sender name matches (contains or is contained by)
            return !(mSender.includes(senderLower) || senderLower.includes(mSender));
          });
          
          const removedCount = (prev.pendingMatches || []).length - remainingMatches.length;
          
          return {
            ...prev,
            pendingMatches: remainingMatches,
            stats: {
              ...prev.stats,
              needs_confirmation: Math.max(0, (prev.stats?.needs_confirmation || 0) - removedCount)
            }
          };
        });
      } else {
        const err = await response.json();
        alert(err.detail || 'Failed to ignore');
      }
    } catch (err) {
      console.error('Ignore error:', err);
      alert(err.message);
    } finally {
      setProcessingMatch(null);
    }
  };
  
  const filteredCustomersForSelect = allCustomers.filter(c => {
    if (!customerSearchTerm) return true;
    const term = customerSearchTerm.toLowerCase();
    return (
      c.name?.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term) ||
      c.id?.toLowerCase().includes(term)
    );
  });
  
  // Apply VAT to amount if enabled
  const applyVat = (amount) => {
    if (!vatEnabled || !amount) return amount;
    return amount * (1 + vatPercentage / 100);
  };
  
  // Fetch VAT settings
  useEffect(() => {
    const fetchVatSettings = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const response = await fetch(`${API_URL}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setVatEnabled(data.vat_enabled || false);
          setVatPercentage(data.vat_percentage || 20);
        }
      } catch (error) {
        console.error('Error fetching VAT settings:', error);
      }
    };
    fetchVatSettings();
  }, []);
  
  // Fetch owings from API
  const fetchOwings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.getOwings();
      setOwingsData(data);
    } catch (err) {
      console.error('Failed to fetch owings:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchOwings();
  }, [fetchOwings]);

  // Close the mobile kebab dropdown when the user taps anywhere outside it.
  // Each menu marks its trigger + popover with `data-mobile-actions-menu`
  // so this listener can ignore clicks within them.
  useEffect(() => {
    if (!openMobileMenuFor) return;
    const handleClick = (e) => {
      if (!e.target.closest('[data-mobile-actions-menu]')) {
        setOpenMobileMenuFor(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [openMobileMenuFor]);

  // Get customers with outstanding balances
  const owingCustomers = useMemo(() => {
    if (!owingsData?.customers) return [];
    
    // Use customer ID hash for the distance placeholder (no real lat/lng yet).
    const hashCode = (str) => {
      let hash = 0;
      for (let i = 0; i < (str || '').length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      return Math.abs(hash);
    };
    
    // Compute REAL days-owed from the oldest unpaid invoice the backend now
    // returns (previously this was a fake `hash % 90` value, which is why
    // every customer showed nonsensical 80+ day overdue badges).
    const daysSince = (iso) => {
      if (!iso) return null;
      const ts = new Date(iso).getTime();
      if (Number.isNaN(ts)) return null;
      const diffMs = Date.now() - ts;
      return Math.max(0, Math.floor(diffMs / 86400000));
    };
    
    let customers = owingsData.customers.map(c => {
        const hash = hashCode(c.id || c.name);
        const realDebtAge = daysSince(c.oldest_invoice_date);
        return {
          ...c,
          // Null when there's no invoice on record (e.g. legacy balance with
          // no matching invoice doc) — UI hides the badge in that case.
          debtAge: realDebtAge,
          distance: ((hash % 50) / 10 + 0.5).toFixed(2),
        };
      });

    switch (sortBy) {
      case 'debt-oldest':
        customers.sort((a, b) => (b.debtAge ?? -1) - (a.debtAge ?? -1));
        break;
      case 'debt-newest':
        customers.sort((a, b) => (a.debtAge ?? Infinity) - (b.debtAge ?? Infinity));
        break;
      case 'amount-high':
        customers.sort((a, b) => b.balance - a.balance);
        break;
      case 'amount-low':
        customers.sort((a, b) => a.balance - b.balance);
        break;
      case 'alphabetical':
        customers.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }

    if (searchTerm) {
      customers = customers.filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.address?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return customers;
  }, [owingsData, sortBy, searchTerm]);

  // Calculate totals (with VAT applied if enabled)
  const totals = useMemo(() => {
    if (!owingsData) return { total: 0, over30Days: 0, over60Days: 0, count: 0 };
    const total = applyVat(owingsData.total_owed || 0);
    const over30Days = owingCustomers.filter(c => c.debtAge > 30).reduce((sum, c) => sum + (c.balance || 0), 0);
    const over60Days = owingCustomers.filter(c => c.debtAge > 60).reduce((sum, c) => sum + (c.balance || 0), 0);
    return { total, over30Days, over60Days, count: owingsData.customer_count || 0 };
  }, [owingsData, owingCustomers, vatEnabled, vatPercentage]);

  const sortOptions = [
    { id: 'debt-oldest', label: 'Oldest First' },
    { id: 'debt-newest', label: 'Newest First' },
    { id: 'amount-high', label: 'Highest Amount' },
    { id: 'amount-low', label: 'Lowest Amount' },
    { id: 'alphabetical', label: 'A-Z' },
  ];

  const getDebtAgeColor = (days) => {
    if (days > 60) return 'text-red-600 bg-red-50';
    if (days > 30) return 'text-amber-600 bg-amber-50';
    return 'text-emerald-600 bg-emerald-50';
  };

  const getDebtAgeBorder = (days) => {
    if (days > 60) return 'border-l-red-500';
    if (days > 30) return 'border-l-amber-500';
    return 'border-l-emerald-500';
  };

  return (
    <div className="h-full flex flex-col bg-zinc-100" data-testid="owings-page">
      {/* Modern Header — top padding deliberately capped instead of using
          the full env(safe-area-inset-top) value. On iOS phones with a Dynamic
          Island that inset can be 50+pt, which pushed the stats cards so far
          down that the worker couldn't see the first job's action buttons
          without scrolling. We respect the safe area but cap it at 12px. */}
      <div className="bg-zinc-900 text-white sticky top-0 z-30" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 12px)' }}>
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-1.5">
          <div className="flex items-center gap-3">
            <button 
              onClick={onMenuClick}
              className="p-2 -ml-2 hover:bg-white/10 rounded-xl transition-colors"
            >
              <Menu size={22} strokeWidth={1.5} />
            </button>
            <h1 className="text-xl font-bold tracking-tight">Owings</h1>
            {vatEnabled && (
              <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded-md">
                inc. {vatPercentage}% VAT
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {!isWorker && setCurrentPage && (
              <button
                onClick={() => setCurrentPage('charge-audit')}
                className="flex items-center gap-2 px-3 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-xl transition-colors text-sm font-medium"
                title="View charge audit log"
                data-testid="open-charge-audit-btn"
              >
                <span className="hidden sm:inline">Audit Log</span>
                <span className="sm:hidden">Audit</span>
              </button>
            )}
            {!isWorker && (
              <button 
                onClick={openReconcileModal}
                className="flex items-center gap-2 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-xl transition-colors text-sm font-medium"
                data-testid="find-payments-btn"
              >
                <Banknote size={18} />
                <span className="hidden sm:inline">Find Payments</span>
              </button>
            )}
            <button className="p-2.5 hover:bg-white/10 rounded-xl transition-colors">
              <Filter size={20} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Stats Cards — tightened all paddings + text sizes so the dark
            header doesn't eat half the viewport on phones. */}
        <div className="px-4 pb-2">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2">
              <div className="flex items-center gap-1.5 mb-0.5">
                <PoundSterling size={11} className="text-white/70" />
                <p className="text-white/70 text-[10px] font-medium">Total Owed</p>
              </div>
              <p className="text-base font-bold leading-tight">£{totals.total.toFixed(2)}</p>
              <p className="text-[10px] text-white/60">{totals.count} customers</p>
            </div>
            <div className="bg-amber-500/20 backdrop-blur-sm rounded-lg p-2 border border-amber-400/30">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Clock size={11} className="text-amber-300" />
                <p className="text-amber-300 text-[10px] font-medium">30+ Days</p>
              </div>
              <p className="text-base font-bold text-amber-300 leading-tight">£{totals.over30Days.toFixed(2)}</p>
            </div>
            <div className="bg-red-500/20 backdrop-blur-sm rounded-lg p-2 border border-red-400/30">
              <div className="flex items-center gap-1.5 mb-0.5">
                <AlertTriangle size={11} className="text-red-300" />
                <p className="text-red-300 text-[10px] font-medium">60+ Days</p>
              </div>
              <p className="text-base font-bold text-red-300 leading-tight">£{totals.over60Days.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Sort */}
      <div className="px-4 py-2 bg-white border-b border-zinc-200">
        <div className="relative mb-2">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
            data-testid="owings-search"
          />
        </div>
        
        <div className="relative">
          <button
            onClick={() => setShowSortMenu(!showSortMenu)}
            className="flex items-center gap-2 px-4 py-2.5 bg-zinc-100 hover:bg-zinc-200 rounded-lg text-sm font-medium text-zinc-700 transition-colors"
          >
            <ArrowUpDown size={16} />
            {sortOptions.find(o => o.id === sortBy)?.label}
          </button>
          
          {showSortMenu && (
            <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-zinc-200 py-2 z-20">
              {sortOptions.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => { setSortBy(opt.id); setShowSortMenu(false); }}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-zinc-50 transition-colors ${sortBy === opt.id ? 'text-emerald-600 font-medium bg-blue-50' : 'text-zinc-700'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Customer List */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-3">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Loader2 size={40} className="text-emerald-500 animate-spin mb-4" />
              <p className="text-zinc-600 font-medium">Loading owings...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <AlertTriangle size={40} className="text-red-500 mb-4" />
              <p className="text-zinc-900 font-semibold mb-2">Failed to load owings</p>
              <p className="text-zinc-500 text-sm mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
              >
                <RefreshCw size={16} />
                Retry
              </button>
            </div>
          ) : owingCustomers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <PoundSterling size={40} className="text-emerald-500 mb-4" />
              <p className="text-zinc-900 font-semibold mb-2">No outstanding balances</p>
              <p className="text-zinc-500 text-sm">All customers are paid up!</p>
            </div>
          ) : owingCustomers.map(customer => (
            <div 
              key={customer.id}
              className={`bg-white rounded-xl border-l-4 shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden ${getDebtAgeBorder(customer.debtAge)}`}
              data-testid={`owing-customer-${customer.id}`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div 
                    className="flex items-start gap-3 flex-1 cursor-pointer"
                    onClick={() => handleCustomerClick(customer)}
                  >
                    {/* Avatar */}
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-sm"
                      style={{ backgroundColor: zoneColors[customer.zone] || '#64748b' }}
                    >
                      {customer.name.charAt(0)}
                    </div>
                    
                    <div className="flex-1">
                      <p className="font-semibold text-zinc-900 hover:text-emerald-600 transition-colors">{customer.name}</p>
                      <p className="text-sm text-zinc-500 mt-0.5">
                        {(() => {
                          const addr = customer.address || '';
                          const parts = addr.split(',').map(p => p.trim());
                          return parts.slice(0, 2).join(', ');
                        })()}
                      </p>
                      
                      {/* Tags */}
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        <span 
                          className="text-xs font-medium px-2 py-0.5 rounded-md"
                          style={{ 
                            backgroundColor: `${zoneColors[customer.zone] || '#64748b'}15`,
                            color: zoneColors[customer.zone] || '#64748b'
                          }}
                        >
                          {customer.zone}
                        </span>
                        {customer.debtAge != null && (
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-md ${getDebtAgeColor(customer.debtAge)}`}
                            data-testid="owings-debt-age-badge"
                            title={customer.oldest_invoice_date ? `Oldest unpaid invoice: ${new Date(customer.oldest_invoice_date).toLocaleDateString('en-GB')}` : ''}
                          >
                            Owed ({customer.debtAge} {customer.debtAge === 1 ? 'day' : 'days'})
                          </span>
                        )}
                        {(customer.has_stripe || customer.stripeActive) && (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-md bg-purple-50 text-purple-600" data-testid="owings-stripe-badge">
                            Stripe
                          </span>
                        )}
                        {(customer.has_gocardless || customer.goCardlessActive) && (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-md bg-cyan-50 text-cyan-600" data-testid="owings-gc-badge">
                            GoCardless
                          </span>
                        )}
                        {(() => {
                          const iso = customer.last_chased_at;
                          const ts = iso ? new Date(iso).getTime() : NaN;
                          if (Number.isNaN(ts)) {
                            return (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-md bg-zinc-100 text-zinc-400" data-testid="owings-never-chased-badge">
                                Never chased
                              </span>
                            );
                          }
                          const days = Math.max(0, Math.floor((Date.now() - ts) / 86400000));
                          const label = days === 0 ? 'Chased today' : days === 1 ? 'Chased yesterday' : `Chased ${days}d ago`;
                          return (
                            <span
                              className="text-xs font-medium px-2 py-0.5 rounded-md bg-blue-50 text-blue-600"
                              data-testid="owings-last-chased-badge"
                              title={`Last payment request: ${new Date(iso).toLocaleDateString('en-GB')}${customer.last_chased_method ? ` via ${customer.last_chased_method}` : ''}`}
                            >
                              {label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-xl font-bold text-red-600">£{customer.balance.toFixed(2)}</p>
                    {vatEnabled && (
                      <p className="text-xs text-zinc-400">inc. {vatPercentage}% VAT</p>
                    )}
                    <p className="text-xs text-zinc-400 mt-1">{customer.distance} mi</p>
                  </div>
                </div>

                {/* Invoice delivery status — SMS / WhatsApp / Email tick states */}
                <InvoiceDeliveryRow status={customer.delivery_status} />

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-4 pt-3 border-t border-zinc-100">
                  <div className="flex items-center gap-1 flex-wrap">
                    <button 
                      onClick={(e) => handleCall(customer, e)}
                      className="p-2 hover:bg-zinc-100 rounded-lg transition-colors" 
                      title="Call"
                      data-testid={`call-btn-${customer.id}`}
                    >
                      <Phone size={16} className="text-zinc-400" />
                    </button>
                    <button 
                      onClick={(e) => handleEmail(customer, e)}
                      className="p-2 hover:bg-zinc-100 rounded-lg transition-colors" 
                      title="Email"
                      data-testid={`email-btn-${customer.id}`}
                    >
                      <Mail size={16} className="text-zinc-400" />
                    </button>
                    <button 
                      onClick={(e) => handleSms(customer, e)}
                      className="p-2 hover:bg-zinc-100 rounded-lg transition-colors" 
                      title="SMS"
                      data-testid={`sms-btn-${customer.id}`}
                    >
                      <MessageSquare size={16} className="text-zinc-400" />
                    </button>
                    <button 
                      onClick={(e) => handleWhatsApp(customer, e)}
                      disabled={!customer.phone}
                      className="p-2 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed" 
                      title="WhatsApp Pay Now"
                      data-testid={`wa-btn-${customer.id}`}
                    >
                      <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#25d366]" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                    </button>
                    <button 
                      onClick={(e) => handlePaymentIcon(customer, e)}
                      className="p-2 hover:bg-zinc-100 rounded-lg transition-colors" 
                      title="Record Payment"
                      data-testid={`payment-icon-btn-${customer.id}`}
                    >
                      <CreditCard size={16} className="text-zinc-400" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Take Payment — full button on desktop, hidden on mobile (moves into kebab) */}
                    <button 
                      onClick={(e) => handleTakePayment(customer, e)}
                      className="hidden sm:flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
                      data-testid={`take-payment-btn-${customer.id}`}
                    >
                      <CreditCard size={16} />
                      Take Payment
                    </button>
                    {/* Payment Method Badges */}
                    {(customer.stripe_has_payment_method || customer.gocardless_mandate_id) && (
                      <div className="flex items-center gap-1">
                        {customer.stripe_has_payment_method && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full" title="Stripe card saved">
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3">
                              <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                            </svg>
                            Card
                          </span>
                        )}
                        {customer.gocardless_mandate_id && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full" title="GoCardless mandate active">
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                            DD
                          </span>
                        )}
                      </div>
                    )}
                    {/* Mark as Paid — full button on desktop, hidden on mobile (moves into kebab) */}
                    <button 
                      onClick={(e) => openPaymentModal(customer, e)}
                      className="hidden sm:flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                      data-testid={`mark-paid-btn-${customer.id}`}
                    >
                      <CheckCircle2 size={16} />
                      Mark as Paid
                    </button>
                    {/* Request Payment — primary CTA, visible on all screens */}
                    <button 
                      onClick={(e) => openRequestPaymentModal(customer, e)}
                      className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors flex-1 sm:flex-none justify-center"
                      data-testid={`request-payment-btn-${customer.id}`}
                    >
                      Request Payment
                      <ChevronRight size={16} />
                    </button>
                    {/* Mobile-only kebab menu — holds Take Payment + Mark as Paid */}
                    <div className="relative sm:hidden" data-mobile-actions-menu>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMobileMenuFor(prev => prev === customer.id ? null : customer.id);
                        }}
                        className="p-2 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors"
                        aria-label="More actions"
                        data-testid={`mobile-actions-btn-${customer.id}`}
                      >
                        <MoreVertical size={18} className="text-zinc-600" />
                      </button>
                      {openMobileMenuFor === customer.id && (
                        <div
                          className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-xl shadow-lg border border-zinc-200 overflow-hidden z-20"
                          data-mobile-actions-menu
                          data-testid={`mobile-actions-menu-${customer.id}`}
                        >
                          <button
                            onClick={(e) => {
                              setOpenMobileMenuFor(null);
                              handleTakePayment(customer, e);
                            }}
                            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-zinc-700 hover:bg-purple-50 hover:text-purple-700 transition-colors text-left"
                            data-testid={`mobile-take-payment-btn-${customer.id}`}
                          >
                            <CreditCard size={16} />
                            Take Payment
                          </button>
                          <button
                            onClick={(e) => {
                              setOpenMobileMenuFor(null);
                              openPaymentModal(customer, e);
                            }}
                            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-zinc-700 hover:bg-green-50 hover:text-green-700 transition-colors text-left border-t border-zinc-100"
                            data-testid={`mobile-mark-paid-btn-${customer.id}`}
                          >
                            <CheckCircle2 size={16} />
                            Mark as Paid
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Payment Modal Component */}
      <PaymentModal
        isOpen={showPaymentModal}
        customer={selectedCustomer}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedCustomer(null);
        }}
        onConfirm={handlePaymentConfirm}
        onNavigateToCustomer={(customer) => {
          if (onSelectCustomer) {
            onSelectCustomer(customer);
          }
        }}
        applyVat={applyVat}
        isProcessing={isProcessingPayment}
      />
      
      {/* Request Payment Modal */}
      {showRequestPaymentModal && requestPaymentCustomer && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowRequestPaymentModal(false)}
          data-testid="request-payment-modal-overlay"
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="request-payment-modal"
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Send size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Request Payment</h3>
                    <p className="text-white/80 text-sm">{requestPaymentCustomer.name}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowRequestPaymentModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  data-testid="close-request-payment-modal"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            {requestPaymentSuccess ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 size={32} className="text-green-600" />
                </div>
                <h4 className="text-xl font-bold text-zinc-900 mb-2">Request Sent!</h4>
                <p className="text-zinc-500">
                  Payment request sent to {requestPaymentCustomer.name}
                </p>
              </div>
            ) : requestPaymentPreview ? (
              // ----- PREVIEW SCREEN -----
              // Renders exactly what the customer will see, broken out by
              // channel. User can step back to change wording / channel
              // selection (the Payment Reminder Templates live in Settings →
              // Messaging) before tapping "Confirm & Send" which actually
              // fires the unified send endpoint.
              <div className="p-6 flex-1 min-h-0 overflow-y-auto" data-testid="request-payment-preview">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-5 text-sm text-emerald-800 flex items-start gap-2">
                  <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />
                  <span>Review the wording below. Edit your templates anytime in <strong>Settings → Messaging → Payment Reminders</strong>.</span>
                </div>
                
                {/* SMS preview */}
                {(requestPaymentMethod === 'sms' || requestPaymentMethod === 'both' || requestPaymentMethod === 'all') && (
                  <div className="mb-5" data-testid="preview-sms-block">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare size={16} className="text-zinc-500" />
                      <span className="text-sm font-semibold text-zinc-700">SMS</span>
                      <span className="text-xs text-zinc-400">→ {requestPaymentCustomer.phone}</span>
                    </div>
                    <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-3 text-sm text-zinc-700 whitespace-pre-wrap break-words" data-testid="preview-sms-body">
                      {requestPaymentPreview.sms_body}
                    </div>
                  </div>
                )}
                
                {/* Email preview */}
                {(requestPaymentMethod === 'email' || requestPaymentMethod === 'both' || requestPaymentMethod === 'all') && (
                  <div className="mb-5" data-testid="preview-email-block">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail size={16} className="text-zinc-500" />
                      <span className="text-sm font-semibold text-zinc-700">Email</span>
                      <span className="text-xs text-zinc-400">→ {requestPaymentCustomer.email}</span>
                    </div>
                    <div className="bg-zinc-50 border border-zinc-200 rounded-xl overflow-hidden">
                      <div className="px-3 py-2 bg-zinc-100 border-b border-zinc-200 text-xs">
                        <span className="text-zinc-500">Subject:</span>{' '}
                        <span className="font-medium text-zinc-800" data-testid="preview-email-subject">
                          {requestPaymentPreview.email_subject}
                        </span>
                      </div>
                      {requestPaymentPreview.email_html ? (
                        <iframe
                          title="Email preview"
                          srcDoc={requestPaymentPreview.email_html}
                          className="w-full bg-white"
                          style={{ height: '440px', border: 'none' }}
                          data-testid="preview-email-html"
                        />
                      ) : (
                        <div className="p-3 text-sm text-zinc-700 whitespace-pre-wrap break-words max-h-64 overflow-y-auto" data-testid="preview-email-body">
                          {requestPaymentPreview.email_body}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* WhatsApp preview — uses Meta-approved template so we show
                    a fixed message (the actual template body) rather than
                    the user's editable wording. */}
                {(requestPaymentMethod === 'whatsapp' || requestPaymentMethod === 'all') && (
                  <div className="mb-5" data-testid="preview-whatsapp-block">
                    <div className="flex items-center gap-2 mb-2">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#25d366]" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      <span className="text-sm font-semibold text-zinc-700">WhatsApp</span>
                      <span className="text-xs text-zinc-400">→ {requestPaymentCustomer.phone}</span>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-zinc-700">
                      <p className="text-xs text-zinc-500 mb-2 italic">Sends via Meta-approved <strong>payment_reminder</strong> template:</p>
                      <p className="whitespace-pre-wrap break-words">
                        Hi {requestPaymentCustomer.name?.split(' ')[0] || 'there'}, you have an outstanding balance of £{requestPaymentPreview.balance?.toFixed(2)}. Tap below to pay now.
                      </p>
                      <p className="text-xs text-emerald-700 mt-2">
                        [Button: Pay Now] → {requestPaymentPreview.pay_link || '(no pay link)'}
                      </p>
                      <p className="text-xs text-zinc-500 mt-2 italic">
                        Falls back to SMS if Meta rejects the template.
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setRequestPaymentPreview(null)}
                    className="flex-1 px-4 py-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-medium rounded-xl transition-colors"
                    data-testid="preview-back-btn"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSendRequestPayment}
                    disabled={isRequestingPayment}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-zinc-300 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors"
                    data-testid="preview-confirm-send-btn"
                  >
                    {isRequestingPayment ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Confirm &amp; Send
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-6 flex-1 min-h-0 overflow-y-auto">
                {/* Amount Info — reflects the ticked invoices below */}
                {(() => {
                  const partial = chaseInvoices.length > 0 && chaseSelectedIds.length > 0 && chaseSelectedIds.length < chaseInvoices.length;
                  const selectedTotal = partial
                    ? chaseInvoices.filter(i => chaseSelectedIds.includes(i.id)).reduce((s, i) => s + (parseFloat(i.total) || 0), 0)
                    : requestPaymentCustomer.balance;
                  return (
                    <div className="bg-red-50 rounded-xl p-4 mb-6">
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-600 text-sm">Amount to Request</span>
                        <span className="text-2xl font-bold text-red-600" data-testid="request-payment-amount">
                          £{selectedTotal.toFixed(2)}
                        </span>
                      </div>
                      {partial && (
                        <p className="text-xs text-zinc-500 text-right mt-1" data-testid="request-payment-total-owed">
                          of £{requestPaymentCustomer.balance.toFixed(2)} total owed
                        </p>
                      )}
                    </div>
                  );
                })()}
                
                {/* Customer Contact Details */}
                <div className="bg-zinc-50 rounded-xl p-4 mb-6 space-y-2">
                  <p className="font-medium text-zinc-900">{requestPaymentCustomer.name}</p>
                  {requestPaymentCustomer.phone && (
                    <p className="text-sm text-zinc-500 flex items-center gap-2">
                      <Phone size={14} /> {requestPaymentCustomer.phone}
                    </p>
                  )}
                  {requestPaymentCustomer.email && (
                    <p className="text-sm text-zinc-500 flex items-center gap-2">
                      <Mail size={14} /> {requestPaymentCustomer.email}
                    </p>
                  )}
                </div>
                
                {/* Send Method Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-zinc-700 mb-3">
                    How would you like to send the request?
                  </label>
                  <div className="space-y-2">
                    <button
                      onClick={() => setRequestPaymentMethod('email')}
                      disabled={!requestPaymentCustomer.email}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                        requestPaymentMethod === 'email'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-zinc-200 hover:border-zinc-300'
                      } ${!requestPaymentCustomer.email ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className={`p-2 rounded-lg ${requestPaymentMethod === 'email' ? 'bg-emerald-100' : 'bg-zinc-100'}`}>
                        <Mail size={20} className={requestPaymentMethod === 'email' ? 'text-emerald-600' : 'text-zinc-500'} />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-zinc-900">Email Only</p>
                        <p className="text-xs text-zinc-500">
                          {requestPaymentCustomer.email || 'No email address'}
                        </p>
                      </div>
                      {requestPaymentMethod === 'email' && <CheckCircle2 size={20} className="text-emerald-600" />}
                    </button>
                    
                    <button
                      onClick={() => setRequestPaymentMethod('sms')}
                      disabled={!requestPaymentCustomer.phone}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                        requestPaymentMethod === 'sms'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-zinc-200 hover:border-zinc-300'
                      } ${!requestPaymentCustomer.phone ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className={`p-2 rounded-lg ${requestPaymentMethod === 'sms' ? 'bg-emerald-100' : 'bg-zinc-100'}`}>
                        <MessageSquare size={20} className={requestPaymentMethod === 'sms' ? 'text-emerald-600' : 'text-zinc-500'} />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-zinc-900">SMS Only</p>
                        <p className="text-xs text-zinc-500">
                          {requestPaymentCustomer.phone || 'No phone number'}
                        </p>
                      </div>
                      {requestPaymentMethod === 'sms' && <CheckCircle2 size={20} className="text-emerald-600" />}
                    </button>
                    
                    <button
                      onClick={() => setRequestPaymentMethod('both')}
                      disabled={!requestPaymentCustomer.email || !requestPaymentCustomer.phone}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                        requestPaymentMethod === 'both'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-zinc-200 hover:border-zinc-300'
                      } ${(!requestPaymentCustomer.email || !requestPaymentCustomer.phone) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid="request-payment-method-both"
                    >
                      <div className={`p-2 rounded-lg ${requestPaymentMethod === 'both' ? 'bg-emerald-100' : 'bg-zinc-100'}`}>
                        <div className="flex -space-x-1">
                          <Mail size={16} className={requestPaymentMethod === 'both' ? 'text-emerald-600' : 'text-zinc-500'} />
                          <MessageSquare size={16} className={requestPaymentMethod === 'both' ? 'text-emerald-600' : 'text-zinc-500'} />
                        </div>
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-zinc-900">Both SMS & Email</p>
                        <p className="text-xs text-zinc-500">Send reminder via both channels</p>
                      </div>
                      {requestPaymentMethod === 'both' && <CheckCircle2 size={20} className="text-emerald-600" />}
                    </button>

                    {/* WhatsApp option - uses the Meta-approved payment_reminder
                        template; auto falls back to SMS if Meta rejects. */}
                    <button
                      onClick={() => setRequestPaymentMethod('whatsapp')}
                      disabled={!requestPaymentCustomer.phone}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                        requestPaymentMethod === 'whatsapp'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-zinc-200 hover:border-zinc-300'
                      } ${!requestPaymentCustomer.phone ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid="request-payment-method-whatsapp"
                    >
                      <div className={`p-2 rounded-lg ${requestPaymentMethod === 'whatsapp' ? 'bg-emerald-100' : 'bg-zinc-100'}`}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={requestPaymentMethod === 'whatsapp' ? 'text-emerald-600' : 'text-zinc-500'}>
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                        </svg>
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-zinc-900">WhatsApp</p>
                        <p className="text-xs text-zinc-500">Sends via Meta template (auto-falls back to SMS)</p>
                      </div>
                      {requestPaymentMethod === 'whatsapp' && <CheckCircle2 size={20} className="text-emerald-600" />}
                    </button>

                    {/* All Channels - belt-and-braces */}
                    <button
                      onClick={() => setRequestPaymentMethod('all')}
                      disabled={!requestPaymentCustomer.phone || !requestPaymentCustomer.email}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                        requestPaymentMethod === 'all'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-zinc-200 hover:border-zinc-300'
                      } ${(!requestPaymentCustomer.phone || !requestPaymentCustomer.email) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid="request-payment-method-all"
                    >
                      <div className={`p-2 rounded-lg ${requestPaymentMethod === 'all' ? 'bg-emerald-100' : 'bg-zinc-100'}`}>
                        <Send size={20} className={requestPaymentMethod === 'all' ? 'text-emerald-600' : 'text-zinc-500'} />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-zinc-900">All Channels</p>
                        <p className="text-xs text-zinc-500">Email + SMS + WhatsApp (maximum coverage)</p>
                      </div>
                      {requestPaymentMethod === 'all' && <CheckCircle2 size={20} className="text-emerald-600" />}
                    </button>
                  </div>
                </div>
                
                {/* Pick which invoices to chase */}
                {chaseInvoices.length > 1 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                      Invoices to chase <span className="text-zinc-400 font-normal">(untick any you're not chasing yet)</span>
                    </label>
                    <div className="space-y-1.5">
                      {chaseInvoices.map(inv => {
                        const checked = chaseSelectedIds.includes(inv.id);
                        return (
                          <button
                            key={inv.id}
                            type="button"
                            onClick={() => setChaseSelectedIds(prev =>
                              checked ? prev.filter(x => x !== inv.id) : [...prev, inv.id]
                            )}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-colors ${
                              checked ? 'border-emerald-500 bg-emerald-50' : 'border-zinc-200 bg-white hover:bg-zinc-50'
                            }`}
                            data-testid={`chase-invoice-${inv.invoice_number}`}
                          >
                            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                              checked ? 'bg-emerald-500 border-emerald-500' : 'border-zinc-300'
                            }`}>
                              {checked && <CheckCircle2 size={14} className="text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-zinc-900 truncate">{inv.invoice_number}</p>
                              <p className="text-xs text-zinc-500">{inv.due_date ? `Due ${inv.due_date}` : (inv.created_at || '').slice(0, 10)}</p>
                            </div>
                            <span className="text-sm font-bold text-zinc-900">£{(inv.total || 0).toFixed(2)}</span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1.5">
                      Chasing £{chaseInvoices.filter(i => chaseSelectedIds.includes(i.id)).reduce((s, i) => s + (i.total || 0), 0).toFixed(2)} of £{chaseInvoices.reduce((s, i) => s + (i.total || 0), 0).toFixed(2)} owed
                    </p>
                  </div>
                )}

                {/* Optional one-off note — included in the email/SMS */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                    Add a note <span className="text-zinc-400 font-normal">(optional — included in what gets sent)</span>
                  </label>
                  <textarea
                    value={requestPaymentNote}
                    onChange={(e) => setRequestPaymentNote(e.target.value)}
                    rows={2}
                    placeholder="e.g. This covers June's window cleans — thanks!"
                    className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-900 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none resize-none"
                    data-testid="request-payment-note-input"
                  />
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowRequestPaymentModal(false)}
                    className="flex-1 px-4 py-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-medium rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={fetchRequestPaymentPreview}
                    disabled={isLoadingPreview || (chaseInvoices.length > 1 && chaseSelectedIds.length === 0)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-zinc-300 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors"
                    data-testid="send-request-btn"
                  >
                    {isLoadingPreview ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Loading…
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Preview
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Bank Reconciliation Modal */}
      {showReconcileModal && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={(e) => {
            // Only close if clicking directly on overlay, not on date picker popups
            if (e.target === e.currentTarget && reconcileStep !== 'searching') {
              closeReconcileModal();
            }
          }}
          data-testid="reconcile-modal-overlay"
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl modal-shell-h overflow-hidden flex flex-col my-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="reconcile-modal"
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Banknote size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Find Bank Payments</h3>
                    <p className="text-white/80 text-sm">
                      {reconcileStep === 'date-select' ? 'Select date range to search' :
                       reconcileStep === 'searching' ? 'Scanning transactions...' :
                       reconcileData?.bank_name || 'Match results'}
                    </p>
                  </div>
                </div>
                {reconcileStep !== 'searching' && (
                  <button 
                    onClick={closeReconcileModal}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto">
              
              {/* Step 1: Date Selection */}
              {reconcileStep === 'date-select' && (
                <div className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Calendar size={32} className="text-blue-600" />
                    </div>
                    <h4 className="text-lg font-bold text-zinc-900 mb-2">Select Date Range</h4>
                    <p className="text-zinc-500 text-sm">Choose the period to search for incoming payments</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-2">From Date</label>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-2">To Date</label>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  {/* Bank Selector - show if multiple banks */}
                  {connectedBanks.length > 0 && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-zinc-700 mb-2">Select Bank Account</label>
                      <select
                        value={selectedBankId}
                        onChange={(e) => setSelectedBankId(e.target.value)}
                        className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      >
                        {connectedBanks.map((bank) => (
                          <option key={bank.id} value={bank.id}>
                            {bank.bank_name} - {bank.account_name}
                          </option>
                        ))}
                      </select>
                      {connectedBanks.length > 0 && (
                        <button
                          onClick={async () => {
                            const token = localStorage.getItem('streakfree_token');
                            const res = await fetch(`${API_URL}/api/truelayer/connect`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (res.ok) {
                              const data = await res.json();
                              if (data.authorization_url) {
                                window.location.href = data.authorization_url;
                              }
                            }
                          }}
                          className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          + Add another bank account
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* No banks connected message */}
                  {connectedBanks.length === 0 && (
                    <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <p className="text-amber-800 text-sm">
                        No bank connected. Please connect a bank via Sync Centre first.
                      </p>
                    </div>
                  )}
                  
                  {/* Quick date buttons */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {[
                      { label: 'Last 7 days', days: 7 },
                      { label: 'Last 30 days', days: 30 },
                      { label: 'Last 60 days', days: 60 },
                      { label: 'Last 90 days', days: 90 }
                    ].map(({ label, days }) => (
                      <button
                        key={days}
                        onClick={() => {
                          const to = new Date();
                          const from = new Date();
                          from.setDate(from.getDate() - days);
                          setDateFrom(toLocalYMD(from));
                          setDateTo(toLocalYMD(to));
                        }}
                        className="px-3 py-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-sm rounded-lg transition-colors"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={startReconcileSearch}
                    disabled={connectedBanks.length === 0}
                    className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Search size={20} />
                    Search Transactions
                  </button>
                </div>
              )}
              
              {/* Step 2: Searching Animation */}
              {reconcileStep === 'searching' && (
                <div className="p-8">
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                      <Loader2 size={40} className="text-blue-600 animate-spin" />
                    </div>
                    <h4 className="text-lg font-bold text-zinc-900 mb-2">Scanning Bank Transactions</h4>
                    <p className="text-zinc-500 text-sm">{searchProgress.currentTxn}</p>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="mb-4">
                    <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300"
                        style={{ width: `${searchProgress.current}%` }}
                      />
                    </div>
                    <p className="text-center text-sm text-zinc-500 mt-2">
                      {Math.round(searchProgress.current)}% complete
                    </p>
                  </div>
                  
                  {/* Animated transaction lines */}
                  <div className="space-y-2 mt-6">
                    {[1, 2, 3, 4].map((i) => (
                      <div 
                        key={i} 
                        className="h-3 bg-zinc-100 rounded animate-pulse"
                        style={{ 
                          width: `${60 + Math.random() * 40}%`,
                          animationDelay: `${i * 0.15}s`
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Step 3: Results */}
              {reconcileStep === 'results' && (
                <>
                  {reconcileData?.status === 'no_bank' ? (
                    <div className="p-8 text-center">
                      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertTriangle size={32} className="text-amber-600" />
                      </div>
                      <h4 className="text-lg font-bold text-zinc-900 mb-2">No Bank Connected</h4>
                      <p className="text-zinc-500 mb-4">Connect your bank via Sync Centre to auto-find payments</p>
                      <button
                        onClick={closeReconcileModal}
                        className="px-4 py-2 bg-zinc-200 text-zinc-700 rounded-lg"
                      >
                        Close
                      </button>
                    </div>
                  ) : reconcileData?.status === 'error' ? (
                    <div className="p-8 text-center">
                      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <X size={32} className="text-red-600" />
                      </div>
                      <h4 className="text-lg font-bold text-zinc-900 mb-2">Error</h4>
                      <p className="text-zinc-500 mb-4">{reconcileData.message}</p>
                      <button
                        onClick={() => setReconcileStep('date-select')}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg"
                      >
                        Try Again
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Summary Stats */}
                      <div className="px-6 py-4 bg-gradient-to-r from-emerald-50 to-blue-50 border-b border-zinc-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-zinc-600">Search complete</p>
                            <p className="text-lg font-bold text-zinc-900">
                              {(reconcileData?.appliedMatches?.length || 0) + (reconcileData?.pendingMatches?.length || 0)} payments found
                            </p>
                          </div>
                          {reconcileData?.stats?.auto_applied_count > 0 && (
                            <div className="text-right">
                              <p className="text-sm text-emerald-600 font-medium">
                                £{reconcileData.stats.auto_applied_total?.toFixed(2)} auto-matched
                              </p>
                              <p className="text-xs text-zinc-500">
                                {reconcileData.stats.auto_applied_count} payments applied
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Auto-Matched Section */}
                      {reconcileData?.appliedMatches?.length > 0 && (
                        <div className="border-b border-zinc-200">
                          <div className="px-6 py-3 bg-emerald-50 flex items-center gap-2">
                            <CheckCircle2 size={18} className="text-emerald-600" />
                            <span className="font-semibold text-emerald-800">
                              Successfully Matched ({reconcileData.appliedMatches.length})
                            </span>
                            <span className="text-emerald-600 text-sm ml-auto">
                              Removed from owing list
                            </span>
                          </div>
                          <div className="divide-y divide-zinc-100 max-h-48 overflow-y-auto">
                            {reconcileData.appliedMatches.map((match) => (
                              <div key={match.transaction_id} className="px-6 py-3 flex items-center justify-between bg-emerald-50/30">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                                    <Check size={16} className="text-emerald-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-zinc-900">{match.customer?.name}</p>
                                    <p className="text-xs text-zinc-500">
                                      {match.merchant_name || match.description?.slice(0, 30) || 'Bank transfer'}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-emerald-600">+£{match.amount.toFixed(2)}</p>
                                  <p className="text-xs text-zinc-500">
                                    {match.match_type === 'auto_ref' ? 'Ref match' : 'Learned'}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Needs Review Section */}
                      {reconcileData?.pendingMatches?.length > 0 && (
                        <div>
                          <div className="px-6 py-3 bg-amber-50 flex items-center gap-2">
                            <AlertTriangle size={18} className="text-amber-600" />
                            <span className="font-semibold text-amber-800">
                              Needs Your Review ({reconcileData.pendingMatches.length})
                            </span>
                          </div>
                          <div className="divide-y divide-zinc-100">
                            {reconcileData.pendingMatches.map((match) => {
                              const isUnmatched = !match.customer;
                              
                              return (
                              <div 
                                key={match.transaction_id} 
                                className={`p-4 ${isUnmatched ? 'bg-zinc-50/50' : ''}`}
                              >
                                {selectingCustomerFor === match.transaction_id ? (
                                  // Customer selection view
                                  <div>
                                    <div className="flex items-center justify-between mb-3">
                                      <span className="font-medium text-zinc-900">Select customer for £{match.amount.toFixed(2)}</span>
                                      <button
                                        onClick={() => setSelectingCustomerFor(null)}
                                        className="text-zinc-400 hover:text-zinc-600"
                                      >
                                        <X size={18} />
                                      </button>
                                    </div>
                                    <div className="relative mb-3">
                                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                                      <input
                                        type="text"
                                        placeholder="Search customers..."
                                        value={customerSearchTerm}
                                        onChange={(e) => setCustomerSearchTerm(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                    </div>
                                    <div className="max-h-48 overflow-y-auto space-y-1">
                                      {filteredCustomersForSelect.slice(0, 10).map((customer) => (
                                        <button
                                          key={customer.id}
                                          onClick={() => confirmMatch(match, customer.id, true)}
                                          disabled={processingMatch === match.transaction_id}
                                          className="w-full flex items-center justify-between p-2 hover:bg-blue-50 rounded-lg text-left text-sm transition-colors disabled:opacity-50"
                                        >
                                          <div>
                                            <p className="font-medium text-zinc-900">{customer.name}</p>
                                            <p className="text-xs text-zinc-500">{customer.email}</p>
                                          </div>
                                          <span className="text-red-600 font-medium">
                                            £{(customer.balance || 0).toFixed(2)}
                                          </span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  // Normal match view
                                  <div className={`flex items-center justify-between gap-4 ${isUnmatched ? 'opacity-60' : ''}`}>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className={`font-bold ${isUnmatched ? 'text-zinc-500' : 'text-emerald-600'}`}>+£{match.amount.toFixed(2)}</span>
                                        <span className="text-xs text-zinc-400">{match.date}</span>
                                        {match.match_type === 'suggested' && (
                                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                            Suggested
                                          </span>
                                        )}
                                        {isUnmatched && (
                                          <span className="text-xs bg-zinc-200 text-zinc-600 px-2 py-0.5 rounded-full">
                                            No match
                                          </span>
                                        )}
                                      </div>
                                      <p className={`text-sm font-medium truncate ${isUnmatched ? 'text-zinc-500' : 'text-zinc-900'}`}>
                                        {match.sender_name || match.merchant_name || match.description || 'Bank transfer'}
                                      </p>
                                      {match.description && match.description !== match.sender_name && (
                                        <p className="text-xs text-zinc-400 truncate">{match.description}</p>
                                      )}
                                      {match.customer && (
                                        <p className="text-xs text-zinc-500 mt-1">
                                          → <span className="font-medium">{match.customer.name}</span>
                                          <span className="text-red-500 ml-1">(owes £{match.customer.balance?.toFixed(2)})</span>
                                        </p>
                                      )}
                                    </div>
                                    
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      {match.customer ? (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() => confirmMatch(match)}
                                            disabled={processingMatch === match.transaction_id}
                                            className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1"
                                            data-testid={`accept-btn-${match.transaction_id}`}
                                          >
                                            {processingMatch === match.transaction_id ? (
                                              <Loader2 size={14} className="animate-spin" />
                                            ) : (
                                              <CheckCircle2 size={14} />
                                            )}
                                            Accept
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setSelectingCustomerFor(match.transaction_id);
                                              setCustomerSearchTerm('');
                                            }}
                                            className="px-3 py-1.5 bg-zinc-200 hover:bg-zinc-300 text-zinc-700 text-sm font-medium rounded-lg transition-colors"
                                            data-testid={`change-btn-${match.transaction_id}`}
                                          >
                                            Change
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => ignoreTransaction(match)}
                                            disabled={processingMatch === match.transaction_id}
                                            className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                                            data-testid={`ignore-matched-btn-${match.transaction_id}`}
                                          >
                                            Ignore
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setSelectingCustomerFor(match.transaction_id);
                                              setCustomerSearchTerm('');
                                            }}
                                            className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors"
                                            data-testid={`find-customer-btn-${match.transaction_id}`}
                                          >
                                            Find Customer
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => ignoreTransaction(match)}
                                            disabled={processingMatch === match.transaction_id}
                                            className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                                            data-testid={`ignore-btn-${match.transaction_id}`}
                                          >
                                            Ignore
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* All Done State */}
                      {(!reconcileData?.appliedMatches?.length && !reconcileData?.pendingMatches?.length) && (
                        <div className="p-8 text-center">
                          <CheckCircle2 size={48} className="text-emerald-500 mx-auto mb-4" />
                          <h4 className="text-lg font-bold text-zinc-900 mb-2">All Caught Up!</h4>
                          <p className="text-zinc-500">No new payments found in this date range</p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 bg-zinc-50 border-t border-zinc-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <p className="text-xs text-zinc-500">
                  {reconcileStep === 'results' 
                    ? 'Tip: Add your customer ID as the payment reference for instant matching'
                    : 'Payments with customer ID in reference are matched instantly'}
                </p>
                {reconcileStep === 'results' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setReconcileStep('date-select')}
                      className="px-4 py-2 bg-zinc-200 hover:bg-zinc-300 text-zinc-700 text-sm font-medium rounded-lg transition-colors"
                    >
                      Search Again
                    </button>
                    <button
                      onClick={closeReconcileModal}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Done
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* SMS Compose Modal */}
      {showSmsModal && smsCustomer && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowSmsModal(false)}
          data-testid="sms-modal-overlay"
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="sms-modal"
          >
            {/* Modal Header */}
            <div className="bg-[#1e3a5f] text-white px-6 py-4">
              <div className="flex items-center justify-between">
                <button 
                  onClick={() => setShowSmsModal(false)}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
                <h3 className="text-lg font-bold">Send SMS</h3>
                <div className="w-6" />
              </div>
              <div className="mt-3">
                <p className="font-semibold">{smsCustomer.name}</p>
                <p className="text-white/70 text-sm">{smsCustomer.phone}</p>
              </div>
            </div>
            
            <div className="p-6">
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Message
              </label>
              <textarea
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                rows={5}
                className="w-full px-4 py-3 bg-white border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent resize-none"
                placeholder="Type your message..."
                data-testid="sms-message-input"
              />
              <p className="text-xs text-zinc-400 mt-2">
                {smsMessage.length} characters
              </p>
              
              <button
                onClick={handleSendSms}
                disabled={isSendingSms || !smsMessage.trim()}
                className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-4 bg-[#1e3a5f] hover:bg-[#2a4a70] disabled:bg-zinc-300 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors"
                data-testid="send-sms-btn"
              >
                {isSendingSms ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <MessageSquare size={18} />
                    Send SMS
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Take Payment Modal */}
      {showTakePayment && takePaymentCustomer && (
        <TakePaymentModal
          isOpen={showTakePayment}
          onClose={() => {
            setShowTakePayment(false);
            setTakePaymentCustomer(null);
          }}
          customer={takePaymentCustomer}
          onSuccess={() => {
            setShowTakePayment(false);
            setTakePaymentCustomer(null);
            // Refresh the owings list to remove paid customer
            fetchOwings();
          }}
        />
      )}

      {/* WhatsApp Compose Modal — used for payment_reminder with Pay Now button */}
      <WhatsAppComposeModal
        isOpen={showWaModal}
        onClose={(success) => {
          setShowWaModal(false);
          setWaCustomer(null);
          if (success) {
            // If they actually sent a reminder, no need to refresh owings
            // since the balance hasn't changed yet (customer needs to pay first).
          }
        }}
        recipient={waCustomer ? {
          id: waCustomer.id,
          customer_id: waCustomer.id,
          name: waCustomer.name,
          phone: waCustomer.phone,
          balance: waCustomer.balance,
          last_invoice_id: waCustomer.latest_invoice_id || waCustomer.last_invoice_id,
          last_invoice_amount: waCustomer.balance,
        } : null}
        defaultTemplate="payment_reminder"
      />
    </div>
  );
};

export default Owings;
